import { Fancybox } from "@fancyapps/ui";

export function initializationForms() {
    const feedbackForm = document.getElementById(
        "footerForm",
    ) as HTMLFormElement | null;
    const callBackForm = document.getElementById(
        "callbackForm_form",
    ) as HTMLFormElement | null;
    const requestForm = document.getElementById(
        "RequestForm_form",
    ) as HTMLFormElement | null;
    if (feedbackForm) new FooterFeedback(feedbackForm);
    if (callBackForm) new CallBackForm(callBackForm);
    if (requestForm) new RequestForm(requestForm);
}

type FeedbackErrors = Record<string, string[]>;

abstract class Feedback {
    protected form: HTMLFormElement;
    protected action: string;
    protected submitBtn: HTMLButtonElement | HTMLInputElement | null;
    protected widgetId: number | null = null;
    protected recaptcha_site_key: string;
    private submitting = false;

    protected abstract validate(): boolean;

    protected abstract showErrors(errors: FeedbackErrors): void;

    protected abstract showSuccess(data: any): void;

    protected abstract initFields(): void;

    protected constructor(form: HTMLFormElement) {
        this.form = form;
        this.form.noValidate = true;
        this.action = this.form.action;
        this.submitBtn = this.form.querySelector("[type='submit']");

        const meta = document.querySelector<HTMLMetaElement>(
            'meta[name="recaptcha-site-key"]',
        );
        this.recaptcha_site_key = meta?.content.trim() || "";
    }

    protected initialize(): void {
        this.form.addEventListener("submit", async (e) => {
            e.preventDefault();
            await this.submit();
        });

        this.injectRecaptcha();
    }

    protected initPhoneMask(field: HTMLInputElement): void {
        const format = () => {
            const digits = field.value.replace(/\D/g, "");
            let national = digits;

            if (digits.startsWith("7") || digits.startsWith("8")) {
                national = digits.slice(1);
            }

            national = national.slice(0, 10);

            if (!national) {
                field.value = document.activeElement === field ? "+7 (" : "";
                return;
            }

            let value = `+7 (${national.slice(0, 3)}`;
            if (national.length >= 3) value += ")";
            if (national.length > 3) value += ` ${national.slice(3, 6)}`;
            if (national.length > 6) value += `-${national.slice(6, 8)}`;
            if (national.length > 8) value += `-${national.slice(8, 10)}`;

            field.value = value;
        };

        field.addEventListener("focus", () => {
            if (!field.value) field.value = "+7 (";
        });
        field.addEventListener("input", format);
        field.addEventListener("blur", () => {
            if (field.value.replace(/\D/g, "").length <= 1) {
                field.value = "";
            }
        });

        if (field.value) format();
    }

    protected isPhoneValid(field: HTMLInputElement): boolean {
        return /^7\d{10}$/.test(field.value.replace(/\D/g, ""));
    }

    protected isEmailValid(field: HTMLInputElement): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim());
    }

    protected setRequiredFieldsError(
        hasInvalidFields: boolean,
        agreementAccepted: boolean,
    ): void {
        if (hasInvalidFields && !agreementAccepted) {
            this.setFormError(
                "Заполните обязательные поля и подтвердите согласие с политикой конфиденциальности.",
            );
        } else if (hasInvalidFields) {
            this.setFormError("Заполните обязательные поля.");
        } else if (!agreementAccepted) {
            this.setFormError(
                "Подтвердите согласие с политикой конфиденциальности.",
            );
        }
    }

    protected randomId() {
        return `fb-form-${Math.random().toString(36).substr(2, 9)}`;
    }

    protected injectRecaptcha() {
        if (!this.recaptcha_site_key) return;

        const wrapper =
            this.form.querySelector<HTMLDivElement>("div.recaptcha");
        if (wrapper === null) return;

        if (wrapper.querySelector("[data-recaptcha-widget]")) {
            return;
        }

        const container = document.createElement("div");
        container.id = `${this.form.id || this.randomId()}-recaptcha`;
        wrapper.appendChild(container);

        const render = () => {
            if (
                container.dataset.rendered === "true" ||
                typeof grecaptcha === "undefined"
            ) {
                return;
            }

            this.widgetId = grecaptcha.render(container, {
                sitekey: this.recaptcha_site_key,
                theme: "light",
            });
            container.dataset.rendered = "true";
        };

        window.addEventListener("recaptcha:ready", render, { once: true });

        if (typeof grecaptcha !== "undefined") {
            render();
        }
    }

    private getWidgetId(): number | null {
        const nativeWidget = this.form.querySelector<HTMLElement>(
            "[data-recaptcha-widget]",
        );
        const nativeWidgetId = nativeWidget?.dataset.widgetId;

        if (nativeWidgetId !== undefined && nativeWidgetId !== "") {
            return Number(nativeWidgetId);
        }

        return this.widgetId;
    }

    protected getFormErrorField(): HTMLElement | null {
        const dedicatedError = this.form.querySelector<HTMLElement>(
            "[data-form-error].error",
        );
        if (dedicatedError) return dedicatedError;

        const prev = this.submitBtn?.previousElementSibling;
        if (
            prev &&
            prev.tagName === "DIV" &&
            prev.classList.contains("error")
        ) {
            return prev as HTMLElement;
        }
        return null;
    }

    protected getOrCreateErrorField(): HTMLElement {
        let errorElement = this.getFormErrorField();
        if (errorElement) {
            return errorElement;
        } else {
            const dedicatedError = this.form.querySelector<HTMLElement>(
                "[data-form-error]",
            );
            if (dedicatedError) {
                dedicatedError.classList.add("error");
                return dedicatedError;
            }

            errorElement = document.createElement("div");
            errorElement.classList.add("error");
            this.submitBtn?.insertAdjacentElement("beforebegin", errorElement);
            return errorElement;
        }
    }

    protected setFormError(text: string): void {
        if (this.submitBtn) {
            let errorElement = this.getOrCreateErrorField();
            errorElement.innerHTML = text;
        } else {
            console.error("Нет кнопки, перед которой выводить ошибку");
        }
    }

    protected removeFormError(): void {
        const erElement = this.getFormErrorField();
        if (erElement) {
            if (erElement.hasAttribute("data-form-error")) {
                erElement.textContent = "";
                erElement.classList.remove("error");
            } else {
                erElement.remove();
            }
        }
    }

    private setSubmitting(submitting: boolean): void {
        this.submitting = submitting;

        if (!this.submitBtn) return;

        this.submitBtn.disabled = submitting;

        if (this.submitBtn instanceof HTMLInputElement) {
            this.submitBtn.dataset.defaultLabel ||= this.submitBtn.value;
            this.submitBtn.value = submitting
                ? "Отправляем..."
                : this.submitBtn.dataset.defaultLabel;
        } else {
            this.submitBtn.dataset.defaultLabel ||= this.submitBtn.textContent || "";
            this.submitBtn.textContent = submitting
                ? "Отправляем..."
                : this.submitBtn.dataset.defaultLabel;
        }
    }

    private async submit() {
        if (this.submitting) return;

        this.removeFormError();
        if (!this.validate()) return;

        const widgetId = this.getWidgetId();

        if (!this.recaptcha_site_key) {
            this.setFormError(
                "Проверка «Я не робот» временно недоступна. Свяжитесь с нами по телефону.",
            );
            return;
        }

        if (typeof grecaptcha === "undefined" || widgetId === null) {
            this.setFormError(
                "Не удалось загрузить проверку «Я не робот». Обновите страницу или свяжитесь с нами по телефону.",
            );
            return;
        }

        const token = grecaptcha.getResponse(widgetId);

        if (!token) {
            this.setFormError("Пожалуйста, подтвердите, что вы не робот");
            return;
        }

        const formData = new FormData(this.form);
        formData.set("g-recaptcha-response", token);
        this.setSubmitting(true);

        try {
            const response = await fetch(this.action, {
                method: "POST",
                body: formData,
                headers: { Accept: "application/json" },
            });
            const payload = await response.json().catch(() => null);

            if (response.ok && payload?.status === "ok") {
                this.showSuccess(payload);
            } else if (payload?.errors) {
                this.showErrors(payload.errors);
            } else {
                this.setFormError(
                    payload?.message || "Не удалось отправить заявку. Попробуйте ещё раз.",
                );
            }
        } catch (error) {
            console.error("Feedback form submit error", error);
            this.setFormError(
                "Не удалось отправить заявку. Проверьте соединение и попробуйте ещё раз.",
            );
        } finally {
            this.setSubmitting(false);
            grecaptcha.reset(widgetId);
        }
    }
}

class RequestForm extends Feedback {
    phone: HTMLInputElement;
    email: HTMLInputElement;
    name: HTMLInputElement;
    product: HTMLInputElement;
    agreement: HTMLInputElement;

    constructor(form: HTMLFormElement) {
        super(form);
        this.phone = form.querySelector("[name='phone']") as HTMLInputElement;
        this.email = form.querySelector("[name='email']") as HTMLInputElement;
        this.name = form.querySelector("[name='name']") as HTMLInputElement;
        this.product = form.querySelector("[name='product']") as HTMLInputElement;
        this.agreement = form.querySelector(
            "[name='agreement']",
        ) as HTMLInputElement;
        if (!this.name || !this.phone || !this.email || !this.product || !this.agreement) {
            return;
        }
        this.initPhoneMask(this.phone);
        this.initFields();
        this.initialize();
    }

    protected initFields(): void {
        [this.name, this.phone, this.email].forEach((field) =>
            field.addEventListener("focus", () => this.clearError(field)),
        );
        this.agreement.addEventListener("change", () => this.removeFormError());
    }

    private clearError(field: HTMLInputElement): void {
        if (field.classList.contains("error")) {
            field.classList.remove("error");
        }
        const sibling = field.nextElementSibling;
        if (
            sibling &&
            sibling.tagName === "DIV" &&
            sibling.classList.contains("error")
        ) {
            sibling.remove();
        }
    }

    private setFieldError(field: HTMLElement, message: string): void {
        this.clearError(field as HTMLInputElement);
        field.classList.add("error");
        const errorElement = document.createElement("div");
        errorElement.classList.add("error");
        errorElement.innerText = message;
        field.insertAdjacentElement("afterend", errorElement);
    }

    protected validate(): boolean {
        let hasInvalidFields = false;

        if (!this.name.value.trim()) {
            this.setFieldError(this.name, "Укажите имя");
            hasInvalidFields = true;
        }
        if (!this.isPhoneValid(this.phone)) {
            this.setFieldError(this.phone, "Укажите телефон полностью");
            hasInvalidFields = true;
        }
        if (!this.isEmailValid(this.email)) {
            this.setFieldError(this.email, "Укажите корректный E-mail");
            hasInvalidFields = true;
        }

        const isValid = !hasInvalidFields && this.agreement.checked;
        if (!isValid) {
            this.setRequiredFieldsError(
                hasInvalidFields,
                this.agreement.checked,
            );
        }

        return isValid;
    }

    protected showErrors(errors: FeedbackErrors): void {
        Object.entries(errors).forEach(([key, messages]) => {
            const field = this[key as keyof RequestForm] as
                | HTMLInputElement
                | undefined;
            field && key !== "agreement"
                ? this.setFieldError(field, messages.join(" "))
                : this.setFormError(messages.join(" "));
        });
    }

    protected showSuccess(): void {
        this.form.reset();
        Fancybox.close();
        Fancybox.show([{ src: "#callbackSuccess", type: "inline" }]);
    }
}
class CallBackForm extends Feedback {
    phone: HTMLInputElement;
    agreement: HTMLInputElement;

    constructor(form: HTMLFormElement) {
        super(form);
        this.phone = form.querySelector("[name='phone']") as HTMLInputElement;
        this.agreement = form.querySelector(
            "[name='agreement']",
        ) as HTMLInputElement;
        if (!this.phone || !this.agreement) {
            return;
        }
        this.initPhoneMask(this.phone);
        this.initFields();
        this.initialize();
    }

    protected initFields(): void {
        this.phone.addEventListener("focus", () => this.clearError(this.phone));
        this.agreement.addEventListener("change", () => this.removeFormError());
    }

    private clearError(field: HTMLInputElement): void {
        if (field.classList.contains("error")) {
            field.classList.remove("error");
        }
        const sibling = field.nextElementSibling;
        if (
            sibling &&
            sibling.tagName === "DIV" &&
            sibling.classList.contains("error")
        ) {
            sibling.remove();
        }
    }

    private setFieldError(field: HTMLElement, message: string): void {
        this.clearError(field as HTMLInputElement);
        field.classList.add("error");
        const errorElement = document.createElement("div");
        errorElement.classList.add("error");
        errorElement.innerText = message;
        field.insertAdjacentElement("afterend", errorElement);
    }

    protected validate(): boolean {
        let hasInvalidFields = false;

        if (!this.isPhoneValid(this.phone)) {
            this.setFieldError(this.phone, "Укажите телефон полностью");
            hasInvalidFields = true;
        }

        const isValid = !hasInvalidFields && this.agreement.checked;
        if (!isValid) {
            this.setRequiredFieldsError(
                hasInvalidFields,
                this.agreement.checked,
            );
        }

        return isValid;
    }

    protected showErrors(errors: FeedbackErrors): void {
        Object.entries(errors).forEach(([key, messages]) => {
            const field = this[key as keyof CallBackForm] as
                | HTMLInputElement
                | undefined;
            field && key !== "agreement"
                ? this.setFieldError(field, messages.join(" "))
                : this.setFormError(messages.join(" "));
        });
    }

    protected showSuccess(): void {
        this.form.reset();
        Fancybox.close();
        Fancybox.show([{ src: "#callbackSuccess", type: "inline" }]);
    }
}

class FooterFeedback extends Feedback {
    name: HTMLInputElement;
    phone: HTMLInputElement;
    email: HTMLInputElement;
    message: HTMLInputElement;
    agreement: HTMLInputElement;

    constructor(form: HTMLFormElement) {
        super(form);
        this.name = form.querySelector("[name='name']") as HTMLInputElement;
        this.phone = form.querySelector("[name='phone']") as HTMLInputElement;
        this.email = form.querySelector("[name='email']") as HTMLInputElement;
        this.message = form.querySelector(
            "[name='message']",
        ) as HTMLInputElement;
        this.agreement = form.querySelector(
            "[name='agreement']",
        ) as HTMLInputElement;
        if (!this.name || !this.phone || !this.email || !this.message || !this.agreement) {
            return;
        }
        this.initPhoneMask(this.phone);
        this.initFields();
        this.initialize();
    }

    protected initFields(): void {
        [this.name, this.phone, this.email, this.message].forEach((field) =>
            field.addEventListener("focus", () => this.clearError(field)),
        );
        this.agreement.addEventListener("change", () => this.removeFormError());
    }

    private clearError(field: HTMLInputElement): void {
        if (field.classList.contains("error")) {
            field.classList.remove("error");
        }
        const sibling = field.nextElementSibling;
        if (
            sibling &&
            sibling.tagName === "DIV" &&
            sibling.classList.contains("error")
        ) {
            sibling.remove();
        }
    }

    private setFieldError(field: HTMLElement, message: string): void {
        this.clearError(field as HTMLInputElement);
        field.classList.add("error");
        const errorElement = document.createElement("div");
        errorElement.classList.add("error");
        errorElement.innerText = message;
        field.insertAdjacentElement("afterend", errorElement);
    }

    protected validate(): boolean {
        let hasInvalidFields = false;

        if (!this.name.value.trim()) {
            this.setFieldError(this.name, "Укажите имя");
            hasInvalidFields = true;
        }
        if (!this.isPhoneValid(this.phone)) {
            this.setFieldError(this.phone, "Укажите телефон полностью");
            hasInvalidFields = true;
        }
        if (!this.isEmailValid(this.email)) {
            this.setFieldError(this.email, "Укажите корректный E-mail");
            hasInvalidFields = true;
        }
        if (!this.message.value.trim()) {
            this.setFieldError(this.message, "Введите ваш вопрос");
            hasInvalidFields = true;
        }

        const isValid = !hasInvalidFields && this.agreement.checked;
        if (!isValid) {
            this.setRequiredFieldsError(
                hasInvalidFields,
                this.agreement.checked,
            );
        }

        return isValid;
    }

    protected showErrors(errors: FeedbackErrors): void {
        Object.entries(errors).forEach(([key, messages]) => {
            const field = this[key as keyof FooterFeedback] as
                | HTMLInputElement
                | undefined;
            field && key !== "agreement"
                ? this.setFieldError(field, messages.join(" "))
                : this.setFormError(messages.join(" "));
        });
    }

    protected showSuccess(): void {
        this.form.reset();
        Fancybox.close();
        Fancybox.show([{ src: "#callbackSuccess", type: "inline" }]);
    }
}
